/**
 * Cosyflows Cache Service
 *
 * Provides pre-loaded cosyflows data for instant page rendering.
 * - Initializes at app startup and loads from IndexedDB cache
 * - Stores transformed data in memory for instant access
 * - Supports background refresh without blocking UI
 */

import { db } from '../db';
import axios from 'axios';
import { getApiBaseUrl, getAuthorizationToken } from '../api-helper';
import { hasSupportedTypes, getUnsupportedTypes } from '../components/DynamicPromptForm';

// Cache configuration
const CACHE_KEY = 'cosyflows_api_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// In-memory cache for instant access
let cachedCosyflows = null;
let cachedCategoryTariffs = null;
let isInitialized = false;
let initPromise = null;
let lastFetchTimestamp = 0;

// Listeners for cache updates
const listeners = new Set();

/**
 * Subscribe to cache updates
 * @param {Function} callback - Called when cache is updated
 * @returns {Function} Unsubscribe function
 */
export function subscribeToCosyflowsCache(callback) {
    listeners.add(callback);
    return () => listeners.delete(callback);
}

/**
 * Notify all listeners of cache update
 */
function notifyListeners() {
    listeners.forEach(callback => {
        try {
            callback(cachedCosyflows);
        } catch (e) {
            console.error('[CosyflowsCache] Listener error:', e);
        }
    });
}

/**
 * Detect output type from category
 */
function detectOutputType(category) {
    if (!category) return 'image';
    const lower = category.toLowerCase();
    if (lower.includes('video') || lower.includes('animation')) return 'video';
    if (lower.includes('audio') || lower.includes('music') || lower.includes('sound')) return 'audio';
    if (lower.includes('3d') || lower.includes('mesh') || lower.includes('model')) return '3d';
    return 'image';
}

/**
 * Extract image URL from cosyflow data
 */
function extractImageUrl(item) {
    if (item.image_url) return item.image_url;
    if (item.imageUrl) return item.imageUrl;

    if (item.cosyflow) {
        try {
            const cosyflowDef = typeof item.cosyflow === 'string'
                ? JSON.parse(item.cosyflow)
                : item.cosyflow;

            if (cosyflowDef?.image_url) return cosyflowDef.image_url;

            const exampleUrls = cosyflowDef?.metadata?.example_urls;
            const previewImages = cosyflowDef?.metadata?.preview_images;

            if (Array.isArray(exampleUrls) && exampleUrls.length > 0) {
                return exampleUrls[0];
            }
            if (Array.isArray(previewImages) && previewImages.length > 0) {
                return previewImages[0];
            }
        } catch (e) {
            // Ignore parse errors
        }
    }

    return null;
}

/**
 * Transform raw API cosyflow data to the format CosyflowPicker expects
 */
function transformCosyflowData(items) {
    return items.map(item => {
        const imageUrl = extractImageUrl(item);

        let hasComfyWorkflow = false;
        let creditsCost = item.credits_cost;
        let category = item.category || 'General';
        let speed = item.speed;
        let quality = item.quality;
        let popularity = item.popularity || item.usage_count || 0;
        let workers = item.workers || item.num_workers;
        let recommended = item.recommended || false;

        if (item.cosyflow) {
            try {
                const cosyflowDef = typeof item.cosyflow === 'string'
                    ? JSON.parse(item.cosyflow)
                    : item.cosyflow;

                console.log('[cosyflowsCacheService] Checking hasComfyWorkflow for:', item.title);
                console.log('[cosyflowsCacheService] cosyflowDef keys:', Object.keys(cosyflowDef || {}));
                console.log('[cosyflowsCacheService] cosyflowDef.prompt:', cosyflowDef?.prompt);
                console.log('[cosyflowsCacheService] prompt type:', typeof cosyflowDef?.prompt);
                console.log('[cosyflowsCacheService] prompt is object:', typeof cosyflowDef?.prompt === 'object');
                console.log('[cosyflowsCacheService] prompt keys:', cosyflowDef?.prompt && typeof cosyflowDef.prompt === 'object' ? Object.keys(cosyflowDef.prompt) : 'N/A');

                if (cosyflowDef?.prompt && typeof cosyflowDef.prompt === 'object' && Object.keys(cosyflowDef.prompt).length > 0) {
                    hasComfyWorkflow = true;
                }
                console.log('[cosyflowsCacheService] hasComfyWorkflow result:', hasComfyWorkflow);

                if (cosyflowDef?.credits_cost !== undefined) {
                    creditsCost = cosyflowDef.credits_cost;
                }

                if (!item.category && cosyflowDef?.category) {
                    category = cosyflowDef.category;
                }

                const metadata = cosyflowDef?.metadata || {};
                if (metadata.speed !== undefined) speed = metadata.speed;
                if (metadata.quality !== undefined) quality = metadata.quality;
                if (metadata.popularity !== undefined) popularity = metadata.popularity;
                if (metadata.workers !== undefined) workers = metadata.workers;
                if (metadata.recommended !== undefined) recommended = metadata.recommended;
            } catch (e) {
                console.error('[cosyflowsCacheService] Parse error for:', item.title, e);
            }
        } else {
            console.log('[cosyflowsCacheService] No cosyflow data for:', item.title);
        }

        return {
            id: item.id || item.uid,
            uid: item.uid,
            title: item.title || item.name,
            description: item.description || '',
            category: category,
            imageUrl: imageUrl,
            outputType: detectOutputType(category),
            creator: item.alias || item.creator,
            accountNo: item.account_no || item.accountNo,
            trusted: item.trusted === 1 || item.trusted === "1" || item.trusted === true,
            isPublic: item.public === 1,
            credits_cost: creditsCost,
            model: item.title,
            prompt: item.base_prompt || '',
            has_image: !!imageUrl,
            cosyflow: item.cosyflow,
            hasComfyWorkflow: hasComfyWorkflow,
            speed: speed,
            quality: quality,
            popularity: popularity,
            workers: workers,
            recommended: recommended,
            created_date: item.created || item.created_date || item.date_created,
        };
    }).filter(item => {
        // Filter out cosyflows with unsupported variable types
        if (!item.cosyflow) return true;
        try {
            const cosyflowDef = typeof item.cosyflow === 'string'
                ? JSON.parse(item.cosyflow) : item.cosyflow;
            if (cosyflowDef?.variables && !hasSupportedTypes(cosyflowDef.variables)) {
                console.log(`[CosyflowsCache] Hiding "${item.title}" — unsupported types:`, getUnsupportedTypes(cosyflowDef.variables));
                return false;
            }
        } catch (e) { /* parse error, allow through */ }
        return true;
    });
}

/**
 * Load cosyflows from IndexedDB cache
 */
async function loadFromCache() {
    try {
        const cachedData = await db.settings.get(CACHE_KEY);

        if (cachedData?.value) {
            const { cosyflows: rawCosyflows, transformedCosyflows, timestamp } = cachedData.value;
            const cacheAge = Date.now() - timestamp;

            // Use transformed data if available (faster)
            if (transformedCosyflows && transformedCosyflows.length > 0) {
                console.log('[CosyflowsCache] Loaded transformed cache:', transformedCosyflows.length, 'items, age:', Math.round(cacheAge / 1000 / 60), 'minutes');
                lastFetchTimestamp = timestamp;
                return transformedCosyflows;
            }

            // Fall back to transforming raw data
            if (rawCosyflows && rawCosyflows.length > 0) {
                console.log('[CosyflowsCache] Transforming cached raw data:', rawCosyflows.length, 'items');
                const transformed = transformCosyflowData(rawCosyflows);
                lastFetchTimestamp = timestamp;

                // Save transformed data for next time
                try {
                    await db.settings.put({
                        key: CACHE_KEY,
                        value: {
                            cosyflows: rawCosyflows,
                            transformedCosyflows: transformed,
                            timestamp: timestamp
                        }
                    });
                } catch (e) {
                    console.warn('[CosyflowsCache] Failed to save transformed cache:', e);
                }

                return transformed;
            }
        }

        return null;
    } catch (error) {
        console.error('[CosyflowsCache] Error loading from cache:', error);
        return null;
    }
}

/**
 * Fetch fresh cosyflows from API
 */
async function fetchFromAPI() {
    try {
        const apiBase = await getApiBaseUrl();
        console.log('[CosyflowsCache] Fetching from API:', `${apiBase}/api/cosyflows/query`);

        const response = await axios.get(`${apiBase}/api/cosyflows/query`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getAuthorizationToken()}`
            }
        });

        if (response.data && response.data.cosyflows) {
            console.log('[CosyflowsCache] Received', response.data.cosyflows.length, 'cosyflows from API');
            const rawCosyflows = response.data.cosyflows;
            const transformed = transformCosyflowData(rawCosyflows);

            // Cache both raw and transformed data
            try {
                await db.settings.put({
                    key: CACHE_KEY,
                    value: {
                        cosyflows: rawCosyflows,
                        transformedCosyflows: transformed,
                        timestamp: Date.now()
                    }
                });
                console.log('[CosyflowsCache] Cached to IndexedDB');
            } catch (cacheError) {
                console.warn('[CosyflowsCache] Failed to cache:', cacheError);
            }

            lastFetchTimestamp = Date.now();
            return transformed;
        }

        return null;
    } catch (error) {
        console.error('[CosyflowsCache] Error fetching from API:', error);
        return null;
    }
}

/**
 * Fetch category tariffs from API
 */
async function fetchCategoryTariffs() {
    try {
        const apiBase = await getApiBaseUrl();
        const response = await axios.get(`${apiBase}/api/cosyflows/categories`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getAuthorizationToken()}`
            }
        });

        if (response.data?.categories && Array.isArray(response.data.categories)) {
            const tariffMap = new Map();
            response.data.categories.forEach((item) => {
                if (item.cosyflow_category && item.cost !== undefined) {
                    const cost = typeof item.cost === 'string' ? parseFloat(item.cost) : item.cost;
                    if (!isNaN(cost)) {
                        tariffMap.set(item.cosyflow_category, cost);
                    }
                }
            });
            console.log('[CosyflowsCache] Loaded', tariffMap.size, 'category tariffs');
            return tariffMap;
        }
    } catch (error) {
        console.error('[CosyflowsCache] Error fetching category tariffs:', error);
    }
    return new Map();
}

/**
 * Initialize the cache service
 * Called once at app startup
 */
export async function initCosyflowsCache() {
    if (initPromise) {
        return initPromise;
    }

    initPromise = (async () => {
        console.log('[CosyflowsCache] Initializing...');

        // Load from cache first (fast)
        cachedCosyflows = await loadFromCache();

        // Fetch tariffs in parallel
        cachedCategoryTariffs = await fetchCategoryTariffs();

        isInitialized = true;

        // Check if cache is stale and needs refresh
        const cacheAge = Date.now() - lastFetchTimestamp;
        const needsRefresh = !cachedCosyflows || cacheAge > CACHE_DURATION;

        if (cachedCosyflows) {
            console.log('[CosyflowsCache] Initialized with', cachedCosyflows.length, 'cached cosyflows');
            notifyListeners();
        }

        // Background refresh if needed
        if (needsRefresh) {
            console.log('[CosyflowsCache] Cache is stale, refreshing in background...');
            refreshCosyflowsInBackground();
        }

        return cachedCosyflows;
    })();

    return initPromise;
}

/**
 * Refresh cosyflows in background without blocking UI
 */
export async function refreshCosyflowsInBackground() {
    try {
        const freshData = await fetchFromAPI();
        if (freshData && freshData.length > 0) {
            cachedCosyflows = freshData;
            notifyListeners();
            console.log('[CosyflowsCache] Background refresh complete:', freshData.length, 'cosyflows');
        }
    } catch (error) {
        console.error('[CosyflowsCache] Background refresh failed:', error);
    }
}

/**
 * Get cached cosyflows synchronously (returns null if not initialized)
 */
export function getCachedCosyflows() {
    return cachedCosyflows;
}

/**
 * Get cached category tariffs
 */
export function getCachedCategoryTariffs() {
    return cachedCategoryTariffs;
}

/**
 * Check if cache is initialized
 */
export function isCacheInitialized() {
    return isInitialized;
}

/**
 * Get cosyflows - returns cached data immediately if available,
 * otherwise waits for initialization
 */
export async function getCosyflows() {
    if (cachedCosyflows) {
        return cachedCosyflows;
    }

    await initCosyflowsCache();
    return cachedCosyflows || [];
}

/**
 * Force refresh the cache (used after user creates/updates a cosyflow)
 */
export async function forceRefreshCache() {
    console.log('[CosyflowsCache] Force refreshing...');
    const freshData = await fetchFromAPI();
    if (freshData) {
        cachedCosyflows = freshData;
        notifyListeners();
    }
    return cachedCosyflows;
}

/**
 * Remove a single cosyflow from the in-memory cache
 * (IndexedDB cache is handled separately by removeCosyflowFromCaches in useCosyflowHandlers)
 */
export function removeCosyflowFromMemoryCache(uid, title) {
    if (cachedCosyflows) {
        const before = cachedCosyflows.length;
        cachedCosyflows = cachedCosyflows.filter(c =>
            (c.uid || c.id) !== uid && c.title !== title
        );
        if (cachedCosyflows.length < before) {
            console.log('[CosyflowsCache] Removed from in-memory cache:', title);
            notifyListeners();
        }
    }
}

/**
 * Clear the cache (for logout or testing)
 */
export async function clearCosyflowsCache() {
    cachedCosyflows = null;
    cachedCategoryTariffs = null;
    lastFetchTimestamp = 0;
    isInitialized = false;
    initPromise = null;

    try {
        await db.settings.delete(CACHE_KEY);
        console.log('[CosyflowsCache] Cache cleared');
    } catch (e) {
        console.warn('[CosyflowsCache] Failed to clear cache:', e);
    }
}

// Export default object for convenience
const cosyflowsCache = {
    init: initCosyflowsCache,
    getCosyflows,
    getCachedCosyflows,
    getCachedCategoryTariffs,
    isInitialized: isCacheInitialized,
    subscribe: subscribeToCosyflowsCache,
    refresh: refreshCosyflowsInBackground,
    forceRefresh: forceRefreshCache,
    clear: clearCosyflowsCache,
    removeCosyflow: removeCosyflowFromMemoryCache,
};

export default cosyflowsCache;
